import { useState, useCallback, useRef, useEffect } from 'react';
import { AgentExecutionFlow, ProviderExecutionInfo } from '../types';
import { ApiService } from '../services/apiService';
import { useSignalR } from '../contexts/useSignalR';

const STORAGE_KEY = 'selectedProviders';
const MAX_SESSION_TURNS = 5;

export const useMultiProviderQuery = () => {
  const [selectedProviders, setSelectedProviders] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      const parsed = stored ? JSON.parse(stored) : [];
      console.log('Loaded providers from localStorage:', parsed);
      return parsed;
    } catch (error) {
      console.error('Error loading providers from localStorage:', error);
      return [];
    }
  });
  const [providersInitialized, setProvidersInitialized] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [submittedPrompt, setSubmittedPrompt] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [executionFlows, setExecutionFlows] = useState<Record<string, AgentExecutionFlow>>({});
  const [providerExecutions, setProviderExecutions] = useState<ProviderExecutionInfo[]>([]);
  const [liveSearchedOpinions, setLiveSearchedOpinions] = useState<Record<string, any[]>>({});
  const [executionEvents, setExecutionEvents] = useState<Record<string, any[]>>({});
  const [sessionHistory, setSessionHistory] = useState<Array<{ prompt: string; providerExecutions: ProviderExecutionInfo[]; executionFlowsSnapshot: Record<string, AgentExecutionFlow> }>>([]);
  const [providerMaxOpinions, setProviderMaxOpinions] = useState<Record<string, number>>(() => {
    try { const s = localStorage.getItem('provider-max-opinions'); return s ? JSON.parse(s) : {}; } catch { return {}; }
  });
  const [providerMaxContraryOpinions, setProviderMaxContraryOpinions] = useState<Record<string, number>>(() => {
    try { const s = localStorage.getItem('provider-max-contrary-opinions'); return s ? JSON.parse(s) : {}; } catch { return {}; }
  });
  const [providerMaxPassages, setProviderMaxPassages] = useState<Record<string, number>>(() => {
    try { const s = localStorage.getItem('provider-max-passages'); return s ? JSON.parse(s) : {}; } catch { return {}; }
  });
  const [providerEmbeddingIndexes, setProviderEmbeddingIndexes] = useState<Record<string, string>>(() => {
    try { const s = localStorage.getItem('provider-embedding-indexes'); return s ? JSON.parse(s) : {}; } catch { return {}; }
  });
  const [providerHitl, setProviderHitl] = useState<Record<string, boolean>>(() => {
    try { const s = localStorage.getItem('provider-hitl'); return s ? JSON.parse(s) : {}; } catch { return {}; }
  });
  const [providerFolioThreshold, setProviderFolioThreshold] = useState<Record<string, number>>(() => {
    try { const s = localStorage.getItem('provider-folio-threshold'); return s ? JSON.parse(s) : {}; } catch { return {}; }
  });
  const [providerRelevanceCheck, setProviderRelevanceCheck] = useState<Record<string, boolean>>(() => {
    try { const s = localStorage.getItem('provider-relevance-check'); return s ? JSON.parse(s) : {}; } catch { return {}; }
  });
  const [providerReducer, setProviderReducer] = useState<Record<string, boolean>>(() => {
    try { const s = localStorage.getItem('provider-use-reducer'); return s ? JSON.parse(s) : {}; } catch { return {}; }
  });
  const [providerMinSearchScore, setProviderMinSearchScore] = useState<Record<string, number>>(() => {
    try { const s = localStorage.getItem('provider-min-search-score'); return s ? JSON.parse(s) : {}; } catch { return {}; }
  });
  const [providerFlowType, setProviderFlowType] = useState<Record<string, number>>(() => {
    try { const s = localStorage.getItem('provider-flow-type'); return s ? JSON.parse(s) : {}; } catch { return {}; }
  });
  
  // Generate a unique session ID for this browser tab (persist in sessionStorage for tab continuity)
  const [sessionId, setSessionId] = useState<string>(() => {
    const stored = sessionStorage.getItem('researchSessionId');
    if (stored) return stored;
    const newId = crypto.randomUUID();
    sessionStorage.setItem('researchSessionId', newId);
    return newId;
  });

  const [usedSessionContext, setUsedSessionContext] = useState(false);
  // Per-provider session IDs for independent conversation history per provider line
  const providerSessionIdsRef = useRef<Record<string, string>>({});

  // Generate a fresh session ID
  const startNewSession = useCallback(() => {
    const newId = crypto.randomUUID();
    sessionStorage.setItem('researchSessionId', newId);
    setSessionId(newId);
    setUsedSessionContext(false);
    setSessionHistory([]);
    providerSessionIdsRef.current = {};
  }, []);

  // Track if session context is being used (prior conversation history exists)
  useEffect(() => {
    const hasCompletedExecutions = Object.values(executionFlows).some(
      flow => flow.status === 'completed' || flow.status === 'failed' || flow.status === 'blocked' || flow.status === 'noData'
    );
    if (hasCompletedExecutions) {
      setUsedSessionContext(true);
    }
  }, [executionFlows]);

  // Session is continuable only if at least one execution succeeded and under turn limit
  const sessionLimitReached = sessionHistory.length >= MAX_SESSION_TURNS - 1;
  const sessionCanContinue = usedSessionContext && !sessionLimitReached && Object.values(executionFlows).some(
    flow => flow.status === 'completed'
  );
  
  const apiService = ApiService.getInstance();
  const signalRContext = useSignalR();
  const activeExecutionIdsRef = useRef<Set<string>>(new Set());
  const executionFlowsRef = useRef(executionFlows);
  executionFlowsRef.current = executionFlows;
  const providerExecutionsRef = useRef(providerExecutions);
  providerExecutionsRef.current = providerExecutions;
  const submittedPromptRef = useRef(submittedPrompt);
  submittedPromptRef.current = submittedPrompt;

  // Persist selected providers to localStorage
  useEffect(() => {
    if (!providersInitialized) {
      console.log('Skipping save - providers not initialized yet');
      return; // Don't save during initialization
    }
    
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(selectedProviders));
      console.log('Saved providers to localStorage:', selectedProviders, 'Length:', selectedProviders.length);
    } catch (error) {
      console.error('Error saving providers to localStorage:', error);
    }
  }, [selectedProviders, providersInitialized]);

  // Initialize providers on mount
  useEffect(() => {
    const initializeProviders = async () => {
      try {
        const providers = await apiService.getProviders();
        
        // Only set default provider if there's nothing in localStorage
        const stored = localStorage.getItem(STORAGE_KEY);
        console.log('Initialization - stored value:', stored);
        
        // Check if localStorage has a value (even if it's an empty array)
        const hasStoredValue = stored !== null;
        
        if (!hasStoredValue && selectedProviders.length === 0) {
          console.log('No stored value and no selected providers - setting default');
          const defaultProvider = providers.find((p: any) => p.isDefault);
          if (defaultProvider) {
            setSelectedProviders([defaultProvider.id]);
          }
        } else {
          console.log('Using stored/existing providers:', selectedProviders);
        }
        setProvidersInitialized(true);
      } catch (error) {
        console.error('Error initializing providers:', error);
        setProvidersInitialized(true);
      }
    };
    
    initializeProviders();
  }, []);

  // Join session group when SignalR connects
  useEffect(() => {
    if (signalRContext.connectionState.isConnected) {
      signalRContext.joinSessionGroup(sessionId);
    }
    
    return () => {
      if (signalRContext.connectionState.isConnected) {
        signalRContext.leaveSessionGroup(sessionId);
      }
    };
  }, [signalRContext.connectionState.isConnected, sessionId]);

  // Handle execution updates from SignalR
  useEffect(() => {
    const handleExecutionEvent = async (event: any) => {
      // Only process events for active executions
      if (!activeExecutionIdsRef.current.has(event.executionId)) {
        return;
      }

      // Track all events per execution for component consumption
      setExecutionEvents(prev => ({
        ...prev,
        [event.executionId]: [...(prev[event.executionId] || []), event]
      }));

      // Build/update execution flow from events
      setExecutionFlows(prev => {
        const existingFlow = prev[event.executionId] || {
          executionId: event.executionId,
          startTime: event.timestamp,
          userCommand: '',
          userId: event.userId,
          executionSteps: [],
          finalResponse: undefined,
          totalExecutionTimeMs: 0,
          status: 'inProgress',
          searchedOpinions: [],
          searchContexts: []
        };

        const updatedFlow = { ...existingFlow };

        // Update flow based on event type
        switch (event.eventType) {
          case 'ExecutionStarted':
            updatedFlow.userCommand = event.userCommand;
            updatedFlow.startTime = event.timestamp;
            break;

          case 'OrchestratorDecision':
            updatedFlow.orchestratorDecision = event.decision;
            break;

          case 'SearchParametersGenerated':
            updatedFlow.searchContexts = [
              ...updatedFlow.searchContexts,
              {
                vectorQueryText: event.vectorQueryText,
                contraryQueryText: event.contraryQueryText,
                filters: event.filters,
                maxResults: event.maxResults,
                maxPassagesPerDocument: event.maxPassagesPerDocument,
                attemptNumber: event.attemptNumber,
                timestamp: event.timestamp
              }
            ];
            break;

          case 'SearchCompleted':
            // Update the corresponding SearchContext with timing information
            const searchContextIndex = updatedFlow.searchContexts.findIndex(
              (ctx: any) => ctx.attemptNumber === event.attemptNumber
            );
            
            if (searchContextIndex >= 0) {
              updatedFlow.searchContexts[searchContextIndex] = {
                ...updatedFlow.searchContexts[searchContextIndex],
                parameterGenerationTimeMs: event.parameterGenerationTimeMs,
                embeddingGenerationTimeMs: event.embeddingGenerationTimeMs,
                searchExecutionTimeMs: event.searchExecutionTimeMs,
                resultCount: event.resultCount,
                embeddingTokensUsed: event.embeddingTokensUsed,
                cachedEmbeddingTokens: event.cachedEmbeddingTokens
              };
            }
            
            // Add opinions from search
            const existingIds = new Set(updatedFlow.searchedOpinions.map((o: any) => o.id));
            const newOpinions = event.opinions.filter((o: any) => !existingIds.has(o.id));
            updatedFlow.searchedOpinions = [...updatedFlow.searchedOpinions, ...newOpinions];
            
            setLiveSearchedOpinions(prevLive => ({
              ...prevLive,
              [event.executionId]: updatedFlow.searchedOpinions
            }));
            break;

          case 'OpinionsDropped':
            // Mark opinions as dropped after reducer failure
            updatedFlow.searchedOpinions = updatedFlow.searchedOpinions.map((o: any) =>
              event.droppedDocumentIds.includes(o.id)
                ? { ...o, dropReason: event.reason }
                : o
            );
            setLiveSearchedOpinions(prevLive => ({
              ...prevLive,
              [event.executionId]: updatedFlow.searchedOpinions
            }));
            break;

          case 'AgentStepStarted':
            updatedFlow.executionSteps = [
              ...updatedFlow.executionSteps,
              {
                startTime: event.timestamp,
                agentType: event.agentType,
                inputJson: event.inputJson,
                chatHistory: event.chatHistory,
                outputJson: '',
                executionTimeMs: 0,
                modelUsed: event.modelUsed,
                temperature: event.temperature,
                topP: event.topP,
                seed: event.seed,
                frequencyPenalty: event.frequencyPenalty,
                presencePenalty: event.presencePenalty,
                reasoningEffort: event.reasoningEffort,
                systemPromptHash: event.systemPromptHash
              }
            ];
            break;

          case 'AgentStepCompleted':
            const steps = [...updatedFlow.executionSteps];
            const lastStepIndex = steps.findIndex(
              (s: any) => s.agentType === event.agentType && !s.outputJson
            );
            if (lastStepIndex >= 0) {
              steps[lastStepIndex] = {
                ...steps[lastStepIndex],
                outputJson: event.outputJson,
                chatHistory: event.chatHistory || steps[lastStepIndex].chatHistory,
                executionTimeMs: event.executionTimeMs,
                costDetail: event.costDetail,
                modelUsed: event.modelUsed,
                temperature: event.temperature,
                topP: event.topP,
                seed: event.seed,
                frequencyPenalty: event.frequencyPenalty,
                presencePenalty: event.presencePenalty,
                reasoningEffort: event.reasoningEffort,
                errorMessage: event.errorMessage,
                toolCalls: event.toolCalls
              };
              updatedFlow.executionSteps = steps;
            }
            break;

          case 'ExecutionCompleted':
            updatedFlow.endTime = event.timestamp;
            updatedFlow.finalResponse = event.finalResponse;
            updatedFlow.totalExecutionTimeMs = event.totalExecutionTimeMs;
            
            // Map response status to ExecutionStatus (matching backend logic in SignalRAgentExecutionTrackingProvider.cs)
            // Backend uses camelCase serialization, so we use lowercase values
            updatedFlow.status = (() => {
              switch (event.status) {
                case 'success':
                  return 'completed';
                case 'blocked':
                  return 'blocked';
                case 'needs_clarification':
                  return 'needsClarification';
                case 'no_data':
                case 'no_relevant_data':
                case 'no_relevant_results':
                  return 'noData';
                default:
                  return 'failed';
              }
            })();
            
            // Check if all executions are complete
            const allComplete = Array.from(activeExecutionIdsRef.current).every(id => {
              const flow = id === event.executionId ? updatedFlow : prev[id];
              const status = flow?.status;
              return status === 'completed' || status === 'failed' || status === 'blocked' || status === 'needsClarification' || status === 'noData';
            });
            
            if (allComplete) {
              setIsLoading(false);
            }
            break;

          case 'ClarificationRequested':
            updatedFlow.status = 'needsClarification';
            break;

          case 'ClarificationReceived':
            updatedFlow.status = 'inProgress';
            break;
        }

        return {
          ...prev,
          [event.executionId]: updatedFlow
        };
      });
    };

    signalRContext.onExecutionEvent(handleExecutionEvent);
  }, [signalRContext]);

  // Get or create per-provider session IDs for independent conversation history
  const getOrCreateProviderSessionIds = useCallback((providerIds: string[], forceNew = false): Record<string, string> => {
    console.log('[SessionId] getOrCreateProviderSessionIds called', { providerIds, forceNew, currentRef: { ...providerSessionIdsRef.current } });
    const ids = forceNew ? {} : { ...providerSessionIdsRef.current };
    for (const pid of providerIds) {
      if (!ids[pid]) {
        ids[pid] = crypto.randomUUID();
      }
    }
    providerSessionIdsRef.current = ids;
    console.log('[SessionId] result:', ids);
    return ids;
  }, []);

  const submitQuery = useCallback(async (customConfigs?: Array<{ id: string; name: string; config: Record<string, any>; maxOpinions?: number; maxPassagesPerDocument?: number; embeddingIndex?: string }>, options?: { continueSession?: boolean }) => {
    if (!prompt.trim()) return;
    
    // Capture the prompt before clearing
    const promptToSubmit = prompt.trim();
    const isContinue = options?.continueSession === true;
    console.log('[SessionId] submitQuery called', { isContinue, options, hasSubmitted, isLoading });
    
    // If already loading or has previous execution, cancel and reset first
    if (isLoading || hasSubmitted) {
      // Leave all execution groups
      await Promise.all(
        Array.from(activeExecutionIdsRef.current).map(id => 
          signalRContext.leaveExecutionGroup(id)
        )
      );
      
      if (!isContinue) {
        // Clear previous state for new queries
        setExecutionFlows({});
        setProviderExecutions([]);
        setLiveSearchedOpinions({});
        setExecutionEvents({});
        setSessionHistory([]);
      } else {
        // For continue: snapshot current round into history with resolved execution flows
        const currentPrompt = submittedPromptRef.current;
        const currentProviderExecs = providerExecutionsRef.current;
        const currentFlows = executionFlowsRef.current;
        if (currentPrompt && currentProviderExecs.length > 0) {
          const roundFlows: Record<string, AgentExecutionFlow> = {};
          for (const pe of currentProviderExecs) {
            const flow = currentFlows[pe.executionId];
            if (flow) {
              roundFlows[pe.executionId] = { ...flow };
            }
          }
          setSessionHistory(prev => [...prev, { prompt: currentPrompt, providerExecutions: [...currentProviderExecs], executionFlowsSnapshot: roundFlows }]);
        }
      }
      activeExecutionIdsRef.current.clear();
    }
    
    // Store the submitted prompt
    setSubmittedPrompt(promptToSubmit);
    
    // Determine which providers to use
    const providersToUse = [...selectedProviders];
    const customProviderInstances: Array<{ 
      instanceId: string;
      sessionKey: string;
      name: string; 
      config: any;
      useReducer?: boolean;
      enableHitl?: boolean;
      folioThreshold?: number;
      enableRelevanceCheck?: boolean;
      maxOpinions?: number;
      maxContraryOpinions?: number;
      maxPassagesPerDocument?: number;
      embeddingIndex?: string;
      minimumSearchScore?: number;
      flowType?: number;
    }> = [];
    
    // If custom configs are provided, create instances for each
    if (customConfigs && customConfigs.length > 0) {
      customConfigs.forEach(customConfig => {
        // Generate unique instance ID for execution tracking (changes each call)
        const shortUuid = crypto.randomUUID().slice(0, 8);
        const instanceId = `custom-${customConfig.id}-${shortUuid}`;
        
        // Extract useReducer from the reducer config
        const useReducer = customConfig.config.reducer?.useReducer || false;
        
        customProviderInstances.push({
          instanceId,
          sessionKey: `custom-${customConfig.id}`, // Stable key for session continuity
          name: customConfig.name,
          config: {
            customConfigName: customConfig.name,
            orchestrator: customConfig.config.orchestrator,
            researcher: customConfig.config.researcher,
            reducer: customConfig.config.reducer,
            writer: customConfig.config.writer,
            summarizer: customConfig.config.summarizer,
            relevancechecker: customConfig.config.relevancechecker
          },
          useReducer: useReducer,
          enableHitl: (customConfig as any).enableHitl !== false,
          folioThreshold: (customConfig as any).folioThreshold ?? 6,
          enableRelevanceCheck: (customConfig as any).enableRelevanceCheck || false,
          maxOpinions: customConfig.maxOpinions || 5,
          maxContraryOpinions: (customConfig as any).maxContraryOpinions || Math.ceil((customConfig.maxOpinions || 5) / 2),
          maxPassagesPerDocument: customConfig.maxPassagesPerDocument || 3,
          embeddingIndex: customConfig.embeddingIndex,
          minimumSearchScore: (customConfig as any).minimumSearchScore,
          flowType: (customConfig as any).flowType
        });
      });
    }
    
    // Ensure at least one provider is selected
    if (providersToUse.length === 0 && customProviderInstances.length === 0) return;

    setIsLoading(true);
    setHasSubmitted(true);

    try {
      // Generate all session IDs upfront — one call ensures no provider overwrites another
      const allSessionKeys = [
        ...providersToUse,
        ...customProviderInstances.map(i => i.sessionKey)
      ];
      const allSessionIds = getOrCreateProviderSessionIds(allSessionKeys, !isContinue);

      // Submit one query PER provider — each gets its own SessionId for independent conversation history
      const regularProviderExecutions: any[] = [];
      if (providersToUse.length > 0) {
        const responses = await Promise.all(providersToUse.map(async (providerId) => {
          const maxOpinions = providerMaxOpinions[providerId] || 5;
          const maxContraryOpinions = providerMaxContraryOpinions[providerId] || Math.ceil(maxOpinions / 2);
          const maxPassages = providerMaxPassages[providerId] || 3;
          const embeddingIndex = providerEmbeddingIndexes[providerId];
          const enableHitl = providerHitl[providerId] !== false;
          const folioThreshold = providerFolioThreshold[providerId] ?? 6;
          const enableRelevanceCheck = providerRelevanceCheck[providerId] || false;
          const useReducer = providerReducer[providerId] !== false;
          const minSearchScore = providerMinSearchScore[providerId];
          const flowType = providerFlowType[providerId] ?? 0;

          const response = await apiService.submitQueryAsync({
            Prompt: promptToSubmit,
            UserId: undefined,
            SessionId: allSessionIds[providerId],
            ProviderIds: [providerId],
            MaxOpinions: maxOpinions,
            MaxContraryOpinions: maxContraryOpinions,
            MaxPassagesPerDocument: maxPassages,
            EmbeddingIndex: embeddingIndex,
            UseReducer: useReducer,
            EnableHitl: enableHitl,
            FolioThreshold: folioThreshold,
            EnableRelevanceCheck: enableRelevanceCheck,
            MinimumSearchScore: minSearchScore,
            FlowType: flowType,
            ModelOverrides: undefined
          });
          return { providerId, response };
        }));

        // Register all execution IDs first (before joining groups) to prevent
        // race conditions where events arrive before IDs are in activeExecutionIdsRef
        for (const { providerId, response } of responses) {
          const executionId = `${response.executionId}_${providerId}`;
          activeExecutionIdsRef.current.add(executionId);

          regularProviderExecutions.push({
            executionId,
            providerId,
            providerDisplayName: providerId,
            status: 'started',
            modelInfo: {}
          });
        }

        // Now join execution groups (async) — events arriving during this are safe
        // because activeExecutionIdsRef already has all IDs
        await Promise.all(
          regularProviderExecutions.map(pe => signalRContext.joinExecutionGroup(pe.executionId))
        );
      }

      // Submit queries for custom config instances
      const customProviderExecutions: any[] = [];
      for (const instance of customProviderInstances) {
        const response = await apiService.submitQueryAsync({
          Prompt: promptToSubmit,
          UserId: undefined,
          SessionId: allSessionIds[instance.sessionKey],
          ProviderIds: ['custom'],
          MaxOpinions: instance.maxOpinions,
          MaxContraryOpinions: instance.maxContraryOpinions,
          MaxPassagesPerDocument: instance.maxPassagesPerDocument,
          EmbeddingIndex: instance.embeddingIndex,
          UseReducer: instance.useReducer,
          EnableHitl: instance.enableHitl,
          FolioThreshold: instance.folioThreshold,
          EnableRelevanceCheck: instance.enableRelevanceCheck,
          MinimumSearchScore: instance.minimumSearchScore,
          FlowType: instance.flowType ?? 0,
          ModelOverrides: instance.config
        });

        // Use the pre-generated instance ID instead of backend's execution ID
        const backendExecutionId = `${response.executionId}_custom`;
        const instanceExecutionId = instance.instanceId;
        
        activeExecutionIdsRef.current.add(backendExecutionId);

        customProviderExecutions.push({
          executionId: backendExecutionId,
          providerId: instanceExecutionId, // Store instance ID in providerId for identification
          providerDisplayName: instance.name,
          status: 'started',
          modelInfo: {},
          customConfig: {
            config: instance.config,
            useReducer: instance.useReducer,
            maxOpinions: instance.maxOpinions,
            maxContraryOpinions: instance.maxContraryOpinions,
            maxPassagesPerDocument: instance.maxPassagesPerDocument,
            embeddingIndex: instance.embeddingIndex
          }
        });

        await signalRContext.joinExecutionGroup(backendExecutionId);
      }

      // Combine all provider executions
      const allProviderExecutions = [...regularProviderExecutions, ...customProviderExecutions];
      setProviderExecutions(allProviderExecutions);

    } catch (error) {
      console.error('Error submitting query:', error);
      setIsLoading(false);
      setHasSubmitted(false);
      activeExecutionIdsRef.current.clear();
    }
  }, [isLoading, hasSubmitted, prompt, selectedProviders, apiService, signalRContext, providerMaxOpinions, providerMaxContraryOpinions, providerMaxPassages, providerEmbeddingIndexes, providerHitl, providerFolioThreshold, providerRelevanceCheck, providerReducer, providerMinSearchScore, providerFlowType]);

  const clearAndReset = useCallback(async () => {
    // Leave all execution groups
    await Promise.all(
      Array.from(activeExecutionIdsRef.current).map(id => 
        signalRContext.leaveExecutionGroup(id)
      )
    );

    // Reset all state
    setPrompt('');
    setIsLoading(false);
    setHasSubmitted(false);
    setExecutionFlows({});
    setProviderExecutions([]);
    setLiveSearchedOpinions({});
    setExecutionEvents({});
    setSessionHistory([]);
    activeExecutionIdsRef.current.clear();
    providerSessionIdsRef.current = {};
  }, [signalRContext]);

  const refreshProvider = useCallback(async (executionId: string, providerId: string) => {
    if (!submittedPrompt) return;

    try {
      // Leave the old execution group
      await signalRContext.leaveExecutionGroup(executionId);
      
      // Remove from active executions
      activeExecutionIdsRef.current.delete(executionId);

      // Find the provider execution to get custom config if it exists
      const providerExec = providerExecutions.find(pe => pe.executionId === executionId);
      const isCustomProvider = providerId.startsWith('custom-');
      
      let newExecutionId: string;
      
      if (isCustomProvider && providerExec?.customConfig) {
        // Custom provider - resubmit with model overrides and request-level config
        // Extract stable session key: custom-{configId} (strip the random suffix)
        const parts = providerId.split('-');
        const sessionKey = parts.slice(0, -1).join('-'); // Remove last segment (shortUuid)
        const customSessionIds = getOrCreateProviderSessionIds([sessionKey]);
        const response = await apiService.submitQueryAsync({
          Prompt: submittedPrompt,
          UserId: undefined,
          SessionId: customSessionIds[sessionKey],
          ProviderIds: ['custom'],
          MaxOpinions: providerExec.customConfig.maxOpinions,
          MaxContraryOpinions: providerExec.customConfig.maxContraryOpinions || Math.ceil((providerExec.customConfig.maxOpinions || 5) / 2),
          MaxPassagesPerDocument: providerExec.customConfig.maxPassagesPerDocument,
          EmbeddingIndex: providerExec.customConfig.embeddingIndex,
          UseReducer: providerExec.customConfig.useReducer,
          ModelOverrides: providerExec.customConfig.config
        });

        newExecutionId = `${response.executionId}_custom`;
      } else {
        // Regular provider - submit with provider ID and max opinions/passages
        const maxOpinions = providerMaxOpinions[providerId] || 5;
        const maxContraryOpinions = providerMaxContraryOpinions[providerId] || Math.ceil(maxOpinions / 2);
        const maxPassages = providerMaxPassages[providerId] || 3;
        const embeddingIndex = providerEmbeddingIndexes[providerId];
        const useReducer = providerReducer[providerId] !== false;
        const minSearchScore = providerMinSearchScore[providerId];
        const providerSessionIds = getOrCreateProviderSessionIds([providerId]);
        
        const response = await apiService.submitQueryAsync({
          Prompt: submittedPrompt,
          UserId: undefined,
          SessionId: providerSessionIds[providerId],
          ProviderIds: [providerId],
          MaxOpinions: maxOpinions,
          MaxContraryOpinions: maxContraryOpinions,
          MaxPassagesPerDocument: maxPassages,
          EmbeddingIndex: embeddingIndex,
          UseReducer: useReducer,
          MinimumSearchScore: minSearchScore
        });

        newExecutionId = `${response.executionId}_${providerId}`;
      }
      
      // Track the new execution
      activeExecutionIdsRef.current.add(newExecutionId);

      // Update provider executions with new execution ID
      setProviderExecutions(prev => 
        prev.map(pe => 
          pe.executionId === executionId 
            ? { ...pe, executionId: newExecutionId, status: 'started' }
            : pe
        )
      );

      // Clear old execution flow and add placeholder for new one
      setExecutionFlows(prev => {
        const updated = { ...prev };
        delete updated[executionId];
        return updated;
      });

      // Clear old live opinions
      setLiveSearchedOpinions(prev => {
        const updated = { ...prev };
        delete updated[executionId];
        return updated;
      });

      // Join new execution group
      await signalRContext.joinExecutionGroup(newExecutionId);

    } catch (error) {
      console.error('Error refreshing provider:', error);
    }
  }, [submittedPrompt, apiService, signalRContext, providerExecutions, providerMaxOpinions, providerMaxContraryOpinions, providerMaxPassages, providerEmbeddingIndexes, providerReducer, providerMinSearchScore]);

  return {
    selectedProviders,
    setSelectedProviders,
    providersInitialized,
    prompt,
    setPrompt,
    submittedPrompt,
    isLoading,
    hasSubmitted,
    executionFlows,
    providerExecutions,
    liveSearchedOpinions,
    executionEvents,
    submitQuery,
    clearAndReset,
    refreshProvider,
    setProviderMaxOpinions,
    setProviderMaxContraryOpinions,
    setProviderMaxPassages,
    setProviderEmbeddingIndexes,
    providerHitl,
    setProviderHitl,
    providerFolioThreshold,
    setProviderFolioThreshold,
    providerRelevanceCheck,
    setProviderRelevanceCheck,
    providerReducer,
    setProviderReducer,
    providerMinSearchScore,
    setProviderMinSearchScore,
    providerFlowType,
    setProviderFlowType,
    sessionId,
    usedSessionContext,
    sessionCanContinue,
    sessionLimitReached,
    sessionHistory,
    startNewSession
  };
};
